import { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Paper,
  InputBase,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

const pad = (n: number) => String(n).padStart(2, "0");

interface MinTime {
  hour: number;   // 24-hour
  minute: number;
}

interface TimePickerProps {
  field: {
    value?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    name?: string;
  };
  label?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  isFullTimeRequired?: boolean;
  minTime?: MinTime; // restrict past times when today is selected
}

/** Convert 12-hour tempHour + tempPeriod to 24-hour integer */
const to24 = (h: string, period: "AM" | "PM") => {
  let n = Number(h);
  if (period === "AM") return n === 12 ? 0 : n;
  return n === 12 ? 12 : n + 12;
};

const TimePicker = ({
  field,
  label,
  isDisabled,
  required = false,
  error = false,
  helperText,
  isFullTimeRequired = true,
  minTime,
}: TimePickerProps) => {
  const { value, onChange, onBlur } = field;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempHour, setTempHour] = useState<string>("");
  const [tempMinute, setTempMinute] = useState<string>("");
  const [tempPeriod, setTempPeriod] = useState<"AM" | "PM">("AM");

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!anchorEl) return;

    if (value) {
      const [h = "", rest = ""] = value.split(":");
      setTempHour(h);
      setTempMinute(rest.slice(0, 2));
      setTempPeriod(value.includes("PM") ? "PM" : "AM");
    } else {
      const now = new Date();
      let h = now.getHours();
      const m = now.getMinutes();
      const p = h >= 12 ? "PM" : "AM";

      h = h % 12 || 12;

      setTempHour(pad(h));
      setTempMinute(pad(m));
      setTempPeriod(p);
    }
  }, [anchorEl, value]);

  /** Returns true if currently selected temp time is before minTime */
  const isBelowMinTime = () => {
    if (!minTime) return false;

    let cur24: number;
    if (isFullTimeRequired) {
      cur24 = to24(tempHour, tempPeriod);
    } else {
      cur24 = Number(tempHour);
    }

    const curMin = Number(tempMinute);
    if (cur24 < minTime.hour) return true;

    if (cur24 === minTime.hour && curMin < minTime.minute) {
      return true;
    }

    return false;
  };

  const clampToMinTime = (h24: number, minute: number): { h24: number; minute: number } => {
    if (!minTime) return { h24, minute };
    if (h24 < minTime.hour) return { h24: minTime.hour, minute: minTime.minute };
    if (h24 === minTime.hour && minute < minTime.minute) return { h24, minute: minTime.minute };
    return { h24, minute };
  };

  const handleHourUp = () => {
    const newH12 = Number(tempHour) >= 12 ? 1 : Number(tempHour) + 1;
    const newH24 = to24(String(newH12), tempPeriod);
    const clamped = clampToMinTime(newH24, Number(tempMinute));
    const clampedH12 = clamped.h24 === 0 ? 12 : clamped.h24 > 12 ? clamped.h24 - 12 : clamped.h24;
    setTempHour(pad(clampedH12));
    setTempMinute(pad(clamped.minute));
  };

  const handleHourDown = () => {
    const newH12 = Number(tempHour) <= 1 ? 12 : Number(tempHour) - 1;
    const newH24 = to24(String(newH12), tempPeriod);
    const clamped = clampToMinTime(newH24, Number(tempMinute));
    const clampedH12 = clamped.h24 === 0 ? 12 : clamped.h24 > 12 ? clamped.h24 - 12 : clamped.h24;
    setTempHour(pad(clampedH12));
    setTempMinute(pad(clamped.minute));
  };

  const handleMinuteUp = () => {
    const h24 = to24(tempHour, tempPeriod);
    const newMin = (Number(tempMinute || 0) + 1) % 60;
    const clamped = clampToMinTime(h24, newMin);
    setTempMinute(pad(clamped.minute));
  };

  const handleMinuteDown = () => {
    const h24 = to24(tempHour, tempPeriod);
    const newMin = Number(tempMinute || 0) === 0 ? 59 : Number(tempMinute) - 1;
    const clamped = clampToMinTime(h24, newMin);
    setTempMinute(pad(clamped.minute));
  };

  const handlePeriodToggle = () => {
    const newPeriod = tempPeriod === "AM" ? "PM" : "AM";
    const newH24 = to24(tempHour, newPeriod);
    const clamped = clampToMinTime(newH24, Number(tempMinute));
    const clampedH12 = clamped.h24 === 0 ? 12 : clamped.h24 > 12 ? clamped.h24 - 12 : clamped.h24;
    setTempPeriod(newPeriod);
    setTempHour(pad(clampedH12));
    setTempMinute(pad(clamped.minute));
  };

  const handleDone = () => {
    if (!tempHour || !tempMinute) return;
    if (isBelowMinTime()) return; // safety guard

    let time = "";

    if (isFullTimeRequired) {
      time = `${pad(Number(tempHour))}:${pad(Number(tempMinute))} ${tempPeriod}`;
    } else {
      time = `${pad(Number(tempHour))}:${pad(Number(tempMinute))}`;
    }

    onChange(time);
    setAnchorEl(null);
    onBlur?.();
  };

  const handleClose = () => {
    onBlur?.();
    setAnchorEl(null);
  };

  return (
    <>
      <TextField
        variant="standard"
        fullWidth
        label={label}
        placeholder="HH:MM"
        value={value || ""}
        error={error}
        required={required}
        disabled={isDisabled}
        helperText={helperText}
        onClick={(e) => !isDisabled && setAnchorEl(e.currentTarget)}
        slotProps={{
          formHelperText: {
            sx: {
              position: "absolute",
              top: "100%",
              left: 0,
              margin: 0,
              fontSize: "10px",
              lineHeight: "12px",
              whiteSpace: "nowrap",
            },
          },
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) setAnchorEl(e.currentTarget.parentElement?.parentElement || e.currentTarget);
                  }}
                  edge="end"
                  sx={{
                    padding: "4px",
                  }}
                >
                  <AccessTimeOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: error ? "#d32f2f" : "#6b7280",
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          position: "relative",
          "& .MuiInputBase-root": {
            fontSize: "10.5px",
          },
          "& .MuiInputBase-input": {
            cursor: "pointer",
          },
          "& .MuiSvgIcon-root": {
            fontSize: 18,
            color: error ? "#d32f2f" : "#6b7280",
          },
          "& .MuiInput-underline:before": {
            borderBottom: error ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.42)",
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottom: error ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.42)",
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: error ? "#d32f2f" : "#5145e5",
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Paper
          sx={{
            p: 2,
            width: 220,
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)"
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mb={1}
          >
            {/* Hour */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                onClick={handleHourUp}
                size="small" sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowUpIcon color="primary" />
              </IconButton>

              <Box
                sx={{
                  border: "1px solid #d0d7e2",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.8,
                  minWidth: 48,
                  textAlign: "center"
                }}
              >
                <InputBase
                  value={tempHour}
                  placeholder="HH"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    if (v === "" || (Number(v) >= 1 && Number(v) <= 12)) {
                      setTempHour(v);
                    }
                  }}
                  inputProps={{
                    maxLength: 2,
                    inputMode: "numeric",
                    style: { textAlign: "center" },
                  }}
                  sx={{ width: 32 }}
                />
              </Box>

              <IconButton
                size="small"
                onClick={handleHourDown}
                sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowDownIcon color="primary" />
              </IconButton>
            </Box>

            <Typography variant="h6">:</Typography>

            {/* Minute */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <IconButton
                size="small"
                onClick={handleMinuteUp}
                sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowUpIcon color="primary" />
              </IconButton>

              <Box
                sx={{
                  border: "1px solid #d0d7e2",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.8,
                  minWidth: 48,
                  textAlign: "center"
                }}
              >
                <InputBase
                  value={tempMinute}
                  placeholder="MM"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    if (v === "" || Number(v) <= 59) {
                      setTempMinute(v);
                    }
                  }}
                  inputProps={{
                    maxLength: 2,
                    inputMode: "numeric",
                    style: { textAlign: "center" },
                  }}
                  sx={{ width: 32 }}
                />
              </Box>

              <IconButton
                size="small"
                onClick={handleMinuteDown}
                sx={{ height: 32, width: 32 }}
              >
                <KeyboardArrowDownIcon color="primary" />
              </IconButton>
            </Box>

            {/* AM / PM */}
            <Button
              variant="outlined"
              size="small"
              onClick={handlePeriodToggle}
              sx={{
                border: "1px solid #d0d7e2",
                minWidth: 55,
                height: 40,
              }}
            >
              {tempPeriod}
            </Button>
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              onClick={handleDone}
              disabled={isBelowMinTime()}
            >
              Done
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default TimePicker;